/*
 * Look Bundles section
 *
 * Thin orchestration layer on top of existing theme components:
 *  - <popup-component> / <dialog>  : open, close, focus trap, scroll lock (theme)
 *  - <slider-component>            : Flickity gallery (theme)
 *  - <product-form>                : variant resolution, price / availability updates (theme)
 *  - <cart-items>                  : "theme:cart:add" -> Ajax add, loading state, errors,
 *                                    cart refresh, cart count, cart drawer open (theme)
 *
 * This file only keeps track of which products are selected per look,
 * computes the live total and builds the `items[]` payload that the
 * theme's cart logic submits in a single request.
 */
(function () {
  'use strict';

  const selectors = {
    look: '[data-look]',
    dialog: 'dialog',
    cardOpen: '[data-look-open]',
    item: '[data-look-item]',
    slider: '.look-bundles__grid',
    gallery: '[data-look-gallery]',
    slide: '[data-slide]',
    product: '[data-look-product]',
    select: '[data-look-select]',
    form: '[data-look-form]',
    items: '[data-look-items]',
    addButton: '[data-look-add]',
    selectedCount: '[data-look-selected-count]',
    selectedLabel: '[data-look-selected-label]',
    total: '[data-look-total]',
    cartItems: 'cart-items',
    openDialog: 'dialog[open]',
    popup: 'popup-component',
  };

  const attributes = {
    variantId: 'data-variant-id',
    variantPrice: 'data-variant-price',
    variantAvailable: 'data-variant-available',
    wasSelected: 'data-was-selected',
    labelOne: 'data-label-one',
    labelOther: 'data-label-other',
    ariaExpanded: 'aria-expanded',
  };

  const classes = {
    selected: 'is-selected',
    unavailable: 'is-unavailable',
    loading: 'is-loading',
  };

  class LookBundlesSection extends HTMLElement {
    constructor() {
      super();

      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onProductAdded = this.onProductAdded.bind(this);
      this.onPopupClose = this.onPopupClose.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
    }

    connectedCallback() {
      this.looks = Array.from(this.querySelectorAll(selectors.look));

      // Section-scoped, delegated listeners
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('theme:variant:change', this.onVariantChange);

      // Global theme events
      document.addEventListener('theme:product:added', this.onProductAdded);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.looks.forEach((look) => {
        const dialog = look.querySelector(selectors.dialog);
        dialog?.addEventListener('theme:popup:onclose', this.onPopupClose);

        look.querySelectorAll(selectors.product).forEach((row) => this.syncRowAvailability(row));
        this.updateLook(look);
      });
    }

    disconnectedCallback() {
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('theme:variant:change', this.onVariantChange);

      document.removeEventListener('theme:product:added', this.onProductAdded);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.looks?.forEach((look) => {
        look.querySelector(selectors.dialog)?.removeEventListener('theme:popup:onclose', this.onPopupClose);
      });
    }

    /* ------------------------------------------------------------------
     * Popup open / close
     * ---------------------------------------------------------------- */

    getLook(id) {
      return id ? this.querySelector(`${selectors.look}[data-look-id="${CSS.escape(id)}"]`) : null;
    }

    getTrigger(look) {
      const id = look?.getAttribute('data-look-id');
      return id ? this.querySelector(`[data-look-open="${CSS.escape(id)}"]`) : null;
    }

    onPointerDown(event) {
      this.pointerStart = {x: event.clientX, y: event.clientY};
    }

    /**
     * The theme's <grid-slider> lets desktop users drag the cards with the mouse.
     * The click that ends a drag must not open a look.
     */
    wasDragged(event) {
      if (!this.pointerStart || event.detail === 0) return false;

      const dx = Math.abs(event.clientX - this.pointerStart.x);
      const dy = Math.abs(event.clientY - this.pointerStart.y);

      return dx > 6 || dy > 6;
    }

    onClick(event) {
      const trigger = event.target.closest(selectors.cardOpen);
      if (!trigger || !this.contains(trigger)) return;

      event.preventDefault();
      if (this.wasDragged(event)) return;

      const look = this.getLook(trigger.getAttribute('data-look-open'));
      if (!look || typeof look.popupOpen !== 'function') return;
      if (look.querySelector(selectors.dialog)?.open) return;

      if (window.a11y) window.a11y.lastElement = trigger;

      look.popupOpen();
      this.onPopupOpen(look);
    }

    onPopupOpen(look) {
      if (!look) return;

      this.getTrigger(look)?.setAttribute(attributes.ariaExpanded, 'true');

      this.refreshGallery(look);

      this.updateLook(look);
    }

    /**
     * The theme's <slider-component> creates its Flickity instance on connect,
     * while the drawer is still hidden (0 x 0). Once the drawer is visible,
     * (re)initialise or re-measure it and reset to the first slide.
     */
    refreshGallery(look) {
      const gallery = look.querySelector(selectors.gallery);
      if (!gallery) return;

      const slides = gallery.querySelectorAll(selectors.slide);
      if (slides.length <= 1) return;

      const refresh = () => {
        if (!look.querySelector(selectors.dialog)?.open) return;

        if (!gallery.flkty || !gallery.flkty.isActive) {
          // Not initialised (e.g. theme JS defined the element before its slides existed)
          gallery.dispatchEvent(new CustomEvent('theme:slider:init', {bubbles: false}));
        }

        if (gallery.flkty) {
          gallery.flkty.resize();
          gallery.flkty.select(0, false, true);
        }
      };

      refresh();
      requestAnimationFrame(refresh);

      // Once more after the drawer's slide-in animation has finished
      clearTimeout(this.galleryTimer);
      this.galleryTimer = setTimeout(refresh, 650);
    }

    onPopupClose(event) {
      const popup = event.target.closest(selectors.popup);
      if (!popup || !popup.matches(selectors.look) || !this.contains(popup)) return;

      const trigger = this.getTrigger(popup);
      trigger?.setAttribute(attributes.ariaExpanded, 'false');

      // The theme restores focus via a11y.autoFocusLastElement() for keyboard
      // users. Fall back to explicit restoration unless a drawer took over.
      if (!document.querySelector('.drawer.is-open')) {
        trigger?.focus({preventScroll: true});
      }
    }

    onProductAdded() {
      // Added successfully: close any open look popup so the cart drawer is visible
      this.querySelectorAll(selectors.openDialog).forEach((dialog) => {
        const popup = dialog.closest(selectors.popup);
        if (popup && typeof popup.popupClose === 'function') {
          popup.popupClose();
        }
      });
    }

    /* ------------------------------------------------------------------
     * Selection and variants
     * ---------------------------------------------------------------- */

    onChange(event) {
      if (!event.target.matches(selectors.select)) return;

      const row = event.target.closest(selectors.product);
      const look = event.target.closest(selectors.look);

      row?.classList.toggle(classes.selected, event.target.checked);
      event.target.removeAttribute(attributes.wasSelected);

      this.updateLook(look);
    }

    onVariantChange(event) {
      const row = event.target.closest(selectors.product);
      if (!row) return;

      const variant = event.detail?.variant || null;

      row.setAttribute(attributes.variantId, variant ? variant.id : '');
      row.setAttribute(attributes.variantPrice, variant ? variant.price : 0);
      row.setAttribute(attributes.variantAvailable, variant && variant.available ? 'true' : 'false');

      this.syncRowAvailability(row);
      this.updateLook(row.closest(selectors.look));
    }

    /**
     * Keep the checkbox in sync with variant availability:
     * unavailable / sold-out variants can't be selected.
     */
    syncRowAvailability(row) {
      const checkbox = row.querySelector(selectors.select);
      if (!checkbox) return;

      const available = row.getAttribute(attributes.variantAvailable) === 'true' && row.getAttribute(attributes.variantId) !== '';

      if (!available) {
        if (checkbox.checked) {
          checkbox.setAttribute(attributes.wasSelected, '');
          checkbox.checked = false;
        }
        checkbox.disabled = true;
      } else if (checkbox.disabled) {
        checkbox.disabled = false;

        if (checkbox.hasAttribute(attributes.wasSelected)) {
          checkbox.checked = true;
          checkbox.removeAttribute(attributes.wasSelected);
        }
      }

      row.classList.toggle(classes.unavailable, !available);
      row.classList.toggle(classes.selected, checkbox.checked && !checkbox.disabled);
    }

    getSelectedRows(look) {
      return Array.from(look.querySelectorAll(selectors.product)).filter((row) => {
        const checkbox = row.querySelector(selectors.select);
        const variantId = parseInt(row.getAttribute(attributes.variantId), 10);

        return checkbox && checkbox.checked && !checkbox.disabled && row.getAttribute(attributes.variantAvailable) === 'true' && variantId > 0;
      });
    }

    /* ------------------------------------------------------------------
     * Totals + payload
     * ---------------------------------------------------------------- */

    updateLook(look) {
      if (!look) return;

      const rows = this.getSelectedRows(look);
      const total = rows.reduce((sum, row) => sum + (parseInt(row.getAttribute(attributes.variantPrice), 10) || 0), 0);

      const countElement = look.querySelector(selectors.selectedCount);
      const labelElement = look.querySelector(selectors.selectedLabel);
      const totalElement = look.querySelector(selectors.total);
      const button = look.querySelector(selectors.addButton);

      if (countElement) countElement.textContent = rows.length;

      if (labelElement) {
        const one = labelElement.getAttribute(attributes.labelOne);
        const other = labelElement.getAttribute(attributes.labelOther);
        labelElement.textContent = rows.length === 1 ? one : other;
      }

      if (totalElement) {
        totalElement.textContent = this.formatMoney(total);
      }

      if (button && !button.classList.contains(classes.loading)) {
        button.disabled = rows.length === 0;
      }

      this.buildItems(look, rows);
    }

    /**
     * Build the `items[n][id]` / `items[n][quantity]` inputs the theme's
     * FormData based add-to-cart will post in a single request.
     */
    buildItems(look, rows) {
      const holder = look.querySelector(selectors.items);
      if (!holder) return;

      const fragment = document.createDocumentFragment();

      rows.forEach((row, index) => {
        const id = parseInt(row.getAttribute(attributes.variantId), 10);
        if (!id) return;

        const idInput = document.createElement('input');
        idInput.type = 'hidden';
        idInput.name = `items[${index}][id]`;
        idInput.value = id;

        const qtyInput = document.createElement('input');
        qtyInput.type = 'hidden';
        qtyInput.name = `items[${index}][quantity]`;
        qtyInput.value = 1;

        fragment.append(idInput, qtyInput);
      });

      holder.replaceChildren(fragment);
    }

    formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(cents, window.theme.moneyFormat);
      }

      return (cents / 100).toFixed(2);
    }

    /* ------------------------------------------------------------------
     * Add the whole look to the cart (single request)
     * ---------------------------------------------------------------- */

    onSubmit(event) {
      const form = event.target.closest(selectors.form);
      if (!form || !this.contains(form)) return;

      event.preventDefault();

      const look = form.closest(selectors.look);
      const button = form.querySelector(selectors.addButton);
      const rows = this.getSelectedRows(look);

      this.buildItems(look, rows);

      if (!rows.length || !button || button.disabled || button.classList.contains(classes.loading)) return;

      // No Ajax cart on this page (e.g. cart type "page" without a drawer):
      // let the browser post the same multi-item form to /cart/add.
      if (!document.querySelector(selectors.cartItems)) {
        form.submit();
        return;
      }

      // Hand over to the theme's cart logic: loading state, errors, cart
      // refresh, header count and cart drawer are all handled there.
      document.dispatchEvent(
        new CustomEvent('theme:cart:add', {
          detail: {
            button: button,
          },
          bubbles: false,
        })
      );
    }

    /* ------------------------------------------------------------------
     * Theme editor
     * ---------------------------------------------------------------- */

    onBlockSelect(event) {
      const item = event.target?.closest?.(selectors.item);
      if (!item || !this.contains(item)) return;

      // Bring the selected card into view when the looks are in a slider
      const slider = item.closest(selectors.slider);
      if (slider && slider.scrollWidth > slider.clientWidth) {
        slider.scrollTo({top: 0, left: item.offsetLeft, behavior: 'smooth'});
      }

      const look = this.getLook(item.getAttribute('data-look-item'));
      const dialog = look?.querySelector(selectors.dialog);
      if (dialog && !dialog.hasAttribute('open') && typeof look.popupOpen === 'function') {
        look.popupOpen();
        this.onPopupOpen(look);
      }
    }

    onBlockDeselect(event) {
      const item = event.target?.closest?.(selectors.item);
      if (!item || !this.contains(item)) return;

      const look = this.getLook(item.getAttribute('data-look-item'));
      if (!look) return;

      const dialog = look.querySelector(selectors.dialog);
      if (dialog && dialog.hasAttribute('open') && typeof look.popupClose === 'function') {
        look.popupClose();
      }
    }
  }

  if (!customElements.get('look-bundles-section')) {
    customElements.define('look-bundles-section', LookBundlesSection);
  }
})();
